"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import { Info, X } from "lucide-react"
import type { PantryResponse, PreparedBatchesResponse } from "@bitebalance/contracts"
import { useAmyDataRefresh } from "@/lib/useAmyDataRefresh"
import { useApiClient } from "@/api/useApiClient"
import { subscribeServerLanguageChanged } from "@/lib/serverLanguage"
import { useI18n, formatEurFromCents, type Locale } from "@/i18n"
import { LoadingState, ErrorState } from "@/components/state"
import { InventoryList } from "@/components/pantry/InventoryList"
import { preparedStatusText } from "@/components/plan/preparedView"
import { LarderBasket } from "@/components/pantry/LarderBasket"
import { CandidateConfirmSheet } from "@/components/pantry/CandidateConfirmSheet"
import { ManualEntrySheet } from "@/components/pantry/ManualEntrySheet"
import { ScanProductDialog } from "@/components/pantry/ScanProductDialog"
import { usePantryCapture } from "@/lib/usePantryCapture"
import { trackKitchenEvent } from "@/lib/kitchenAnalytics"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import { Button } from "@/components/ui/button"

type LoadStatus = "loading" | "success" | "error"

const EDUCATION_DISMISSED_KEY = "bb_pantry_education_dismissed_v1"

function readEducationDismissed(): boolean {
  try {
    return localStorage.getItem(EDUCATION_DISMISSED_KEY) === "1"
  } catch {
    return false
  }
}

/** "spinach and yogurt" / "spinach, rice and yogurt" — a plain manual join, matching
 *  the mobile twin's own reasoning: no Intl.ListFormat, to avoid depending on a browser
 *  feature this app doesn't otherwise rely on for anything else user-facing. */
function joinNames(names: string[], locale: Locale): string {
  const and = locale === "de" ? "und" : "and"
  if (names.length <= 1) return names[0] ?? ""
  if (names.length === 2) return `${names[0]} ${and} ${names[1]}`
  return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`
}

/**
 * Dismissible education card: why an accurate pantry pays off (Amy plans
 * around it, the shopping list subtracts it, outcome marking keeps it fresh).
 * Dismissal persists per browser; the info icon next to the page title
 * brings it back.
 */
function EducationCard({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useI18n()
  return (
    <div className="relative rounded-bb-lg border border-line bg-cream p-4">
      <button
        type="button"
        aria-label={t("views.outcomeSheet.close")}
        className="absolute top-3 right-3 text-ink-2 hover:text-ink"
        onClick={onDismiss}
      >
        <X className="size-4" aria-hidden="true" />
      </button>
      <h2 className="pr-8 text-sm font-semibold text-ink">{t("views.pantry.educationTitle")}</h2>
      <p className="mt-1 text-sm text-ink-2">{t("views.pantry.educationBody")}</p>
      <Button type="button" variant="outline" className="mt-3 min-h-9" onClick={onDismiss}>
        {t("views.pantry.educationDismiss")}
      </Button>
    </div>
  )
}

/**
 * Kitchen redesign (2026-09-14): the empty state, unified — web twin of
 * mobile's KitchenHero. Replaces the separate opt-in pitch (T2 KITCHEN
 * BAND, ADR-070 category B, founder-approved 2026-09-11) and the plain
 * "nothing yet" empty state with one calm hero. No photography: "calm,
 * premium, immediately understandable" reads as type and two clear
 * actions, not a decorative image, and scanning or adding an item IS the
 * enrollment step now — there is no separate "turn this on" tap to design
 * a pitch around.
 */
function KitchenHero({ onScan, onManual }: { onScan: () => void; onManual: () => void }) {
  const { t } = useI18n()
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-2 rounded-bb-xl border border-line bg-paper p-6 text-center shadow-sm">
      <LarderBasket className="size-14 text-green" />
      <p className="text-sm text-ink-2">{t("views.pantry.emptyHeroSubhead")}</p>
      <Button type="button" className="mt-3 min-h-11 w-full" onClick={onScan}>
        {t("views.pantry.scanButton")}
      </Button>
      <Button type="button" variant="outline" className="min-h-11 w-full" onClick={onManual}>
        {t("views.pantry.addManuallyButton")}
      </Button>
    </div>
  )
}

/**
 * Pantry as its own top-level page (2026-08 Pantry batch, promoted out of
 * the Shop tabs): inventory with inline edit/remove, structured manual
 * entry (with the original free-text path kept as a mode inside the same
 * sheet) and barcode scan via the unchanged extraction → confirmation-token
 * flow, plus the education card. Extraction still never writes; only the
 * user's confirmation persists.
 */
export default function PantryPage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [pantry, setPantry] = useState<PantryResponse | null>(null)
  const [prepared, setPrepared] = useState<PreparedBatchesResponse | null>(null)
  const [educationDismissed, setEducationDismissed] = useState(readEducationDismissed)
  const [manualEntryOpen, setManualEntryOpen] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  // Honest lower-bound savings from the current shopping list (reserved
  // stock × derivable pack price). Quiet: failure or 0 hides the chip.
  const [savedCents, setSavedCents] = useState(0)

  // The extraction/confirmation state machine is shared with the scan dialog
  // (lib/usePantryCapture.ts) — structured manual entry, "type a list," and
  // scan all produce a sourceText string and land in the same confirm sheet.
  const capture = usePantryCapture(result => {
    setPantry(result)
    trackKitchenEvent("kitchen_item_added", { source: "manual" })
  })

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) setStatus("loading")
      // Prepared food is a quiet enhancement: a failed read shows no section content, never an error.
      api
        .getPreparedBatches()
        .then(setPrepared)
        .catch(() => setPrepared(null))
      api
        .getPantry()
        .then(result => {
          setPantry(result)
          setStatus("success")
        })
        .catch(() => {
          if (!quiet) setStatus("error")
        })
      api
        .getCurrentShoppingList()
        .then(list => {
          let cents = 0
          for (const item of list.items) {
            const reserved = item.pantryReserved?.canonical.amount ?? 0
            const packQty = item.selectedPack?.quantity ?? 0
            const buy = item.buyQuantity ?? 0
            const price = item.priceEstimate
            // Per-pack price is only derivable when something was bought at
            // a known price — fully-covered rows are priced €0 on the wire,
            // so this sum is an honest LOWER bound, never an invention.
            if (reserved > 0 && packQty > 0 && buy > 0 && price && price.status !== "unavailable") {
              cents += Math.round((reserved / packQty) * (price.amountCents / buy))
            }
          }
          setSavedCents(cents)
        })
        .catch(() => setSavedCents(0))
    },
    [api]
  )

  useEffect(() => {
    load()
    trackKitchenEvent("kitchen_opened")
  }, [load])

  // Kitchen rows carry SERVER-localised catalogue names, so a language switch
  // has to re-read them; the client dictionary cannot translate them, and must
  // never try (that would be a second, guessed catalogue).
  useEffect(() => subscribeServerLanguageChanged(() => load(true)), [load])

  // ADR-036 finding 6: pantry edits made in chat ("the eggs broke, I threw
  // 3") appear here without a manual reload.
  useAmyDataRefresh(() => load(true))

  function setEducation(dismissed: boolean) {
    setEducationDismissed(dismissed)
    try {
      localStorage.setItem(EDUCATION_DISMISSED_KEY, dismissed ? "1" : "0")
    } catch {
      // Private-mode storage failure — the card simply won't persist its state.
    }
  }

  async function handleManualSubmit(sourceText: string, expiryDate: string | null) {
    const ok = await capture.runExtraction(sourceText, expiryDate)
    if (!ok) setStatus("error")
  }

  async function handleConfirmCandidates(
    candidates: Parameters<typeof capture.confirmCandidates>[0]
  ) {
    const result = await capture.confirmCandidates(candidates)
    if (result.success) setManualEntryOpen(false)
  }

  async function handleUpdateQuantity(
    itemId: string,
    quantity: number,
    expiryDate?: string | null
  ) {
    const result = await api.updatePantryItem(
      itemId,
      expiryDate === undefined ? { quantity } : { quantity, expiryDate }
    )
    setPantry(result)
    trackKitchenEvent("kitchen_item_updated")
  }

  async function handleRemove(itemId: string) {
    const result = await api.removePantryItem(itemId)
    setPantry(result)
    trackKitchenEvent("kitchen_item_removed")
  }

  function openAmyForKitchen(prompt: string) {
    trackKitchenEvent("amy_kitchen_suggestion_clicked")
    openAmyWithPrompt(prompt)
  }

  return (
    <section aria-labelledby="pantry-heading" className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <h1 id="pantry-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {t("views.pantry.title")}
        </h1>
        {educationDismissed && (
          <button
            type="button"
            aria-label={t("views.pantry.educationShow")}
            title={t("views.pantry.educationShow")}
            className="text-ink-2 hover:text-ink"
            onClick={() => setEducation(false)}
          >
            <Info className="size-5" aria-hidden="true" />
          </button>
        )}
      </div>

      {!educationDismissed && <EducationCard onDismiss={() => setEducation(true)} />}

      {status === "loading" ? (
        <LoadingState />
      ) : status === "error" || !pantry ? (
        <ErrorState onRetry={load} />
      ) : (
        (() => {
          const availableItems = pantry.items.filter(item => item.state === "available")
          // A grounded, non-urgent Amy moment — real served names only (never invented),
          // capped at 3 so the sentence stays a sentence. (Web doesn't yet fetch the current
          // plan the way mobile's Kitchen does, so the richer "expires before N planned
          // meals" grounding isn't available here — this is the real-names tier only, a
          // deliberate, smaller scope than mobile's for this pass, not a silent gap.)
          const noticedNames = availableItems.slice(0, 3).map(item => item.displayName)
          return availableItems.length === 0 ? (
            // Kitchen redesign (2026-09-14): the opt-in pitch and the "nothing yet" empty
            // state used to be two different screens for the same underlying situation —
            // collapsed into one calm hero. Scan/Add manually ARE the enrollment step now.
            <KitchenHero
              onScan={() => setScanOpen(true)}
              onManual={() => setManualEntryOpen(true)}
            />
          ) : (
            <div className="flex flex-col gap-4">
              {noticedNames.length > 0 && (
                <div className="flex flex-col gap-2 rounded-bb-lg border border-line bg-cream p-4">
                  <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
                    {t("views.pantry.amyNoticedEyebrow")}
                  </p>
                  <p className="text-sm text-ink">
                    {t(
                      noticedNames.length === 1
                        ? "views.pantry.amyNoticedDisplayOne"
                        : "views.pantry.amyNoticedDisplayMany",
                      { items: joinNames(noticedNames, locale) }
                    )}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="min-h-9 self-start"
                    onClick={() =>
                      openAmyForKitchen(
                        t("views.pantry.amyNoticedChatPrompt", {
                          items: joinNames(noticedNames, locale),
                        })
                      )
                    }
                  >
                    {t("amy.askAmy")}
                  </Button>
                </div>
              )}
              {/* ---- add more: the same primary/secondary pair as the empty hero, compact once there's a list to keep in view ---- */}
              <div className="flex gap-2">
                <Button type="button" className="min-h-10 flex-1" onClick={() => setScanOpen(true)}>
                  {t("views.pantry.scanButton")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-10 flex-1"
                  onClick={() => setManualEntryOpen(true)}
                >
                  {t("views.pantry.addManuallyButton")}
                </Button>
              </div>
              {savedCents > 0 && (
                // The one pantry number people care about — an honest lower
                // bound (only pricable reserved stock counts).
                <div className="flex items-center gap-3 rounded-bb-lg border border-green/40 bg-green/10 px-4 py-2.5">
                  <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full bg-green" />
                  <p className="text-sm text-ink">
                    {t("views.pantry.savedLine", {
                      amount: formatEurFromCents(savedCents, locale),
                    })}
                  </p>
                </div>
              )}
              {capture.confirmError && (
                <p role="alert" className="text-sm text-destructive">
                  {t("views.pantry.confirmError")}
                </p>
              )}
              {/* T21 propagation (ADR-070): the inventory sits on a LARDER ground — a shelf,
                  drawn as light, depicting nothing. The Recipe ingredient list and mobile's
                  Kitchen use the same tone, so the surfaces about stored food read as one place. */}
              <div className="bb-ground-larder">
                <InventoryList
                  items={availableItems}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemove}
                />
              </div>
              {/* Kitchen integration (2026-09-14): no local persistent Amy entry point here —
                  AppShell's LeftRail/BottomNav already give every page, Kitchen included, a
                  global "Ask Amy" launcher. A second, page-local one would be the redundant
                  third Amy entry point the current product model explicitly avoids; the
                  grounded "Amy noticed" card above is the only Kitchen-specific Amy surface. */}
              {/* ADR-065: Prepared Food Inventory is its own concept — cooked
                  portions, no longer available as arbitrary raw ingredients. */}
              <section
                aria-labelledby="prepared-heading"
                className="flex flex-col gap-2"
                data-testid="prepared-section"
              >
                <h2 id="prepared-heading" className="text-sm font-semibold text-ink">
                  {t("views.plan.preparedSectionTitle")}
                </h2>
                {(prepared?.items ?? []).length === 0 ? (
                  <p className="text-sm text-ink-2">{t("views.plan.preparedEmpty")}</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {(prepared?.items ?? []).map(batch => (
                      <li
                        key={batch.id}
                        className="rounded-bb-lg border border-line bg-paper p-3"
                        data-testid={`prepared-item-${batch.batchKey}`}
                      >
                        <p className="font-medium text-ink">{batch.displayName}</p>
                        <p className="text-sm font-semibold text-green">
                          {preparedStatusText(batch, t)}
                        </p>
                        <p className="text-xs text-ink-2">{t("views.plan.preparedKeepsNote")}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )
        })()
      )}

      {manualEntryOpen && (
        <ManualEntrySheet
          extracting={capture.extracting}
          onSubmit={(sourceText, expiryDate) => void handleManualSubmit(sourceText, expiryDate)}
          onCancel={() => setManualEntryOpen(false)}
        />
      )}

      <ScanProductDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onPersisted={result => {
          setPantry(result)
          setScanOpen(false)
        }}
      />

      {capture.extraction && (
        <CandidateConfirmSheet
          extraction={capture.extraction}
          confirming={capture.confirming}
          initialExpiryDate={capture.pendingExpiry}
          onConfirm={candidates => void handleConfirmCandidates(candidates)}
          onCancel={capture.cancelExtraction}
        />
      )}
    </section>
  )
}
